import type { ArtifactPreview } from "~/services/artifacts";
import { ArtifactShell } from "./ArtifactShell";
import { DataframeSplit, DataframeTable } from "./DataframeTable";

// A draft-table artifact points at a warehouse table/view created by the
// data-prep agent. Its preview content carries the warehouse locator (`fqn`,
// `draft_kind`) and — when the snapshot was sampled at creation — a rows preview
// in the same pandas-split shape a dataframe artifact uses. Mirrors the merged
// `content` the conversations preview service returns for `ArtifactType.TABLE`
// (see product/ai/services/conversations/.../preview_service.py `_table_preview`).
// The transform SQL rides on `preview.code` and shows in the shell's Code tab.
interface TableSampleContent {
  data: DataframeSplit;
  displayedRows: number;
  totalRows: number;
  // Whether `totalRows` is the draft's exact count. Absent or false for a live
  // view (no fixed count) and for a snapshot whose count couldn't be read, so
  // the card shows the rows as a sample rather than a definitive total.
  totalRowsKnown?: boolean;
}

function hasSample(content: unknown): content is TableSampleContent {
  if (!content || typeof content !== "object" || Array.isArray(content)) return false;
  const c = content as Record<string, unknown>;
  if (typeof c.displayedRows !== "number" || typeof c.totalRows !== "number") return false;
  const split = c.data;
  if (!split || typeof split !== "object" || Array.isArray(split)) return false;
  const s = split as Record<string, unknown>;
  return Array.isArray(s.columns) && Array.isArray(s.data);
}

interface Locator {
  kindLabel: string;
  fqn: string | null;
}

function readLocator(content: unknown): Locator {
  const c =
    content && typeof content === "object" && !Array.isArray(content)
      ? (content as Record<string, unknown>)
      : {};
  return {
    kindLabel: c.draft_kind === "view" ? "Draft view" : "Draft table",
    fqn: typeof c.fqn === "string" ? c.fqn : null,
  };
}

const INLINE_PAGE_SIZE = 10;
const EXPANDED_PAGE_SIZE = 25;

// The warehouse locator line: what the draft is (table vs view) and its
// fully-qualified name, so a reader knows where to query the real data.
const LocatorLine = ({ locator }: { locator: Locator }) => (
  <div className="flex flex-wrap items-center gap-2 text-xs">
    <span className="rounded bg-muted px-1.5 py-0.5 font-medium text-muted-foreground">
      {locator.kindLabel}
    </span>
    {locator.fqn && (
      <code className="truncate font-mono text-muted-foreground" title={locator.fqn}>
        {locator.fqn}
      </code>
    )}
  </div>
);

export const TableArtifact = ({ preview }: { preview: ArtifactPreview }) => {
  const locator = readLocator(preview.content);

  if (!hasSample(preview.content)) {
    return (
      <ArtifactShell preview={preview}>
        <div className="space-y-2">
          <LocatorLine locator={locator} />
          <div className="py-1 text-sm text-muted-foreground">
            No data preview available for this draft.
          </div>
        </div>
      </ArtifactShell>
    );
  }

  const { data: split, displayedRows, totalRows, totalRowsKnown } = preview.content;

  return (
    <ArtifactShell
      preview={preview}
      expandedContent={
        <div className="space-y-3">
          <LocatorLine locator={locator} />
          <DataframeTable
            split={split}
            pageSize={EXPANDED_PAGE_SIZE}
            displayedRows={displayedRows}
            totalRows={totalRows}
            totalKnown={totalRowsKnown ?? true}
          />
        </div>
      }
    >
      <div className="space-y-3">
        <LocatorLine locator={locator} />
        <DataframeTable
          split={split}
          pageSize={INLINE_PAGE_SIZE}
          displayedRows={displayedRows}
          totalRows={totalRows}
          totalKnown={totalRowsKnown ?? true}
        />
      </div>
    </ArtifactShell>
  );
};
